import { Close as CloseIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import TextField from "components/common/TextField";
import { useEffect, useState } from "react";
import * as bffService from "services/bffService";

const emptyFormData = {
  insuredName: "",
  effectiveDate: "",
  ulLimitMillions: null,
  ueGlxPremiumPreviousYear: null,
  ueEllPremiumPreviousYear: null,
  ueGlxPremiumCurrentYear: null,
  ueEllPremiumCurrentYear: null,
  munichBoundPremiumPreviousYear: null,
  evanstonBoundPremiumPreviousYear: null,
  munichPpmRelativeToUl: null,
  evanstonPpmRelativeToUl: null,
  arkTotalPremiumCurrentYear: null,
};

const CauseCreateRecordDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState(emptyFormData);

  const [
    createProgramSubmission,
  ] = bffService.useCreateProgramSubmissionMutation();

  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData(emptyFormData);
  }, [open]);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const result = await createProgramSubmission({
        program: "CAUSE",
        formData,
      }).unwrap();
      const submissionCurrentYear = result?.submissionCurrentYear;
      const priorSubmissionPreviousYear = result?.priorSubmissionPreviousYear;
      if (!submissionCurrentYear?.filename || !submissionCurrentYear?.state) {
        throw new Error("Missing CAUSE submission data.");
      }

      setFormData(emptyFormData);

      onClose();

      if (onSuccess) {
        onSuccess({
          ...(submissionCurrentYear?.submission || {}),
          filename: submissionCurrentYear?.filename,
          state: submissionCurrentYear?.state,
          insured: submissionCurrentYear?.state?.input?.program?.insured,
          riskdescription:
            submissionCurrentYear?.state?.input?.program?.riskDescription,
          inception: submissionCurrentYear?.state?.input?.program?.inception,
          reference: submissionCurrentYear?.state?.input?.program?.reference,
          transaction:
            submissionCurrentYear?.state?.input?.program?.transaction,
          tags: submissionCurrentYear?.state?.input?.program?.tags,
          priorSubmission: priorSubmissionPreviousYear
            ? {
                ...(priorSubmissionPreviousYear?.submission || {}),
                filename: priorSubmissionPreviousYear?.filename,
                state: priorSubmissionPreviousYear?.state,
                insured:
                  priorSubmissionPreviousYear?.state?.input?.program?.insured,
                riskdescription:
                  priorSubmissionPreviousYear?.state?.input?.program
                    ?.riskDescription,
                inception:
                  priorSubmissionPreviousYear?.state?.input?.program?.inception,
                reference:
                  priorSubmissionPreviousYear?.state?.input?.program?.reference,
                transaction:
                  priorSubmissionPreviousYear?.state?.input?.program
                    ?.transaction,
                tags: priorSubmissionPreviousYear?.state?.input?.program?.tags,
              }
            : null,
        });
      }
    } catch (error) {
      alert("Failed to create CAUSE submission. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const isFormValid =
    formData.effectiveDate &&
    formData.ulLimitMillions &&
    formData.ulLimitMillions > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"md"}>
      <DialogTitle>
        {"Create New CAUSE Workbook Record"}
        <IconButton
          aria-label={"close"}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size={"small"}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: "20px !important" }}>
        <Stack spacing={3}>
          <Typography
            variant={"h6"}
            sx={{ fontSize: "0.95rem", fontWeight: 600 }}
          >
            {"Basic Information"}
          </Typography>

          <TextField
            label={"Insured Name"}
            value={formData.insuredName}
            onChange={(e) =>
              setFormData({ ...formData, insuredName: e.target.value })
            }
            size={"small"}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap>
            <TextField
              label={"Effective Date"}
              type={"date"}
              value={formData.effectiveDate}
              onChange={(e) =>
                setFormData({ ...formData, effectiveDate: e.target.value })
              }
              required
              size={"small"}
              sx={{ flex: 1 }}
            />

            <PrettyNumberTextField
              label={"UL Limit (MM)"}
              value={formData.ulLimitMillions}
              onChangeNumber={(value) =>
                setFormData({ ...formData, ulLimitMillions: value })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />
          </Stack>

          <Typography
            variant={"h6"}
            sx={{ fontSize: "0.95rem", fontWeight: 600, pt: 3 }}
          >
            {"UE Premiums - Previous Year"}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap>
            <PrettyNumberTextField
              label={"UE GLX Prem (Prev Yr)"}
              value={formData.ueGlxPremiumPreviousYear}
              onChangeNumber={(value) =>
                setFormData({ ...formData, ueGlxPremiumPreviousYear: value })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />

            <PrettyNumberTextField
              label={"UE ELL Prem (Prev Yr)"}
              value={formData.ueEllPremiumPreviousYear}
              onChangeNumber={(value) =>
                setFormData({ ...formData, ueEllPremiumPreviousYear: value })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />
          </Stack>

          <Typography
            variant={"h6"}
            sx={{ fontSize: "0.95rem", fontWeight: 600, pt: 3 }}
          >
            {"UE Premiums - Current Year"}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap>
            <PrettyNumberTextField
              label={"UE GLX Prem (Curr Yr)"}
              value={formData.ueGlxPremiumCurrentYear}
              onChangeNumber={(value) =>
                setFormData({ ...formData, ueGlxPremiumCurrentYear: value })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />

            <PrettyNumberTextField
              label={"UE ELL Prem (Curr Yr)"}
              value={formData.ueEllPremiumCurrentYear}
              onChangeNumber={(value) =>
                setFormData({ ...formData, ueEllPremiumCurrentYear: value })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />
          </Stack>

          <Typography
            variant={"h6"}
            sx={{ fontSize: "0.95rem", fontWeight: 600, pt: 3 }}
          >
            {"Bound Premiums"}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap>
            <PrettyNumberTextField
              label={"Munich Bound Prem (Prev Yr)"}
              value={formData.munichBoundPremiumPreviousYear}
              onChangeNumber={(value) =>
                setFormData({
                  ...formData,
                  munichBoundPremiumPreviousYear: value,
                })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />

            <PrettyNumberTextField
              label={"Evanston Bound Prem (Prev Yr)"}
              value={formData.evanstonBoundPremiumPreviousYear}
              onChangeNumber={(value) =>
                setFormData({
                  ...formData,
                  evanstonBoundPremiumPreviousYear: value,
                })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />

            <PrettyNumberTextField
              label={"ARK Gross Prem (Curr Yr)"}
              value={formData.arkTotalPremiumCurrentYear}
              onChangeNumber={(value) =>
                setFormData({ ...formData, arkTotalPremiumCurrentYear: value })
              }
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />
          </Stack>

          <Typography
            variant={"h6"}
            sx={{ fontSize: "0.95rem", fontWeight: 600, pt: 3 }}
          >
            {"Layer Pricing Percentages"}
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap>
            <PrettyNumberTextField
              label={"5M x UE, % UL ppm"}
              value={formData.munichPpmRelativeToUl}
              onChangeNumber={(value) =>
                setFormData({ ...formData, munichPpmRelativeToUl: value })
              }
              suffix={"%"}
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />

            <PrettyNumberTextField
              label={"10M x Prin, % UL ppm"}
              value={formData.evanstonPpmRelativeToUl}
              onChangeNumber={(value) =>
                setFormData({ ...formData, evanstonPpmRelativeToUl: value })
              }
              suffix={"%"}
              variant={"filled"}
              size={"small"}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          onClick={handleCreate}
          color={"primary"}
          sx={{ width: 140 }}
          disabled={!isFormValid || isCreating}
        >
          {isCreating ? "Creating..." : "Create"}
        </Button>
        <Button onClick={onClose} color={"secondary"} sx={{ width: 140 }}>
          {"Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CauseCreateRecordDialog;
